import { AccountInvocationItem, Call, transaction } from 'starknet'
import { CLPosition } from '../model/clpool'
import { fromUint256, getToken, toDisplay, toUint256 } from '../model/tokens/helper'
import { Network } from './token.service'
import BigNumber from 'bignumber.js'
import { getAllCLPoolForFee } from '../model/clpool/helper'
import { getCurrentTick } from './amm_v3'
import { getLegacyLpTokenAddresses, migrateLegacyDestinationRange } from './position'
import { accountBraavosMultisig, accountCairoVersion, getAMMContract, getProvider, numberToCallParam, walletAddress } from './amm'
import { getNFTFactoryAddress } from './nft_pool'
import { Transaction } from '../atoms'

const migrateLegacyPosition_phase0_calls = async (
    network: Network,
    activeAccount: string,
    position: CLPosition,
    _fee: number,
    rangePercent: number
): Promise<{ calls: Array<Call>; pool_token_order: boolean } | undefined> => {
    const fee = _fee * 10000
    try {
        // generate the calls
        // 1. approve the relevant lP token
        // 2. withdraw the entire position from legacy myswap
        // 3. approve both tokens & mint a position in myswap-CL

        const pool_id = position.legacyPoolId
        if (pool_id === undefined) throw 'Unknown legacy pool id'

        // we take 2% slippage hard-coded
        let t0_amount_minus_slippage = BigNumber(position.value.token1AmountWei).multipliedBy('0.98').toFixed(0)
        let t1_amount_minus_slippage = BigNumber(position.value.token2AmountWei).multipliedBy('0.98').toFixed(0)

        let token0 = getToken(network, position.token0)
        let token1 = getToken(network, position.token1)
        // console.log('YSYS fee=', fee, ' token0=', token0, ' token1=', token1)
        if (token0 === undefined || token1 === undefined) throw 'Unknown token0/1'
        const cl_pool = await getAllCLPoolForFee(network, token0, token1, fee)
        cl_pool.currentTick = await getCurrentTick(network, cl_pool.address)
        // console.log('YSYS cl_pool=', cl_pool)
        // check the token order of the pool we are going to migrate into
        let pool_token_order = true
        if (BigNumber(cl_pool.token1.address).eq(BigNumber(position.token0)) && BigNumber(cl_pool.token2.address).eq(BigNumber(position.token1))) {
            console.log('YSYS token order is good!')
        } else {
            // Not sure why this happens -
            // but if the myswap V1 pool and myswap CL pools have different token orders-
            // we need to flip the CL call (the V1 call remains as the tokens come from there)
            // console.log('YSYS token order is reversed!')
            pool_token_order = false
        }
        const { tick_lower, tick_upper } = migrateLegacyDestinationRange(network, cl_pool, rangePercent)
        let lp_token = getLegacyLpTokenAddresses(network)[pool_id - 1]
        const calls: Array<Call> = [
            {
                contractAddress: lp_token,
                entrypoint: 'approve',
                calldata: [getAMMContract(network), ...toUint256(position.legacyLpTokenAmount ?? '0')]
            },
            {
                contractAddress: getAMMContract(network),
                entrypoint: 'withdraw_liquidity',
                calldata: [pool_id, ...toUint256(position.legacyLpTokenAmount ?? '0'), ...toUint256(t0_amount_minus_slippage), ...toUint256(t1_amount_minus_slippage)]
            },
            {
                contractAddress: token0.address,
                entrypoint: 'approve',
                calldata: [getNFTFactoryAddress(network), ...toUint256(t0_amount_minus_slippage)]
            },
            {
                contractAddress: token1.address,
                entrypoint: 'approve',
                calldata: [getNFTFactoryAddress(network), ...toUint256(t1_amount_minus_slippage)]
            },
            {
                contractAddress: getNFTFactoryAddress(network),
                entrypoint: 'mint',
                calldata: [
                    numberToCallParam(activeAccount),
                    numberToCallParam(pool_token_order ? token0.address : token1.address),
                    numberToCallParam(pool_token_order ? token1.address : token0.address),
                    numberToCallParam(fee),
                    numberToCallParam(tick_lower + 887272),
                    numberToCallParam(tick_upper + 887272),
                    ...toUint256(pool_token_order ? t0_amount_minus_slippage : t1_amount_minus_slippage),
                    ...toUint256(pool_token_order ? t1_amount_minus_slippage : t0_amount_minus_slippage),
                    ...toUint256('0'),
                    ...toUint256('0')
                ]
            }
        ]

        return { calls, pool_token_order }
    } catch {
        return undefined
    }
}
export const new_migrateLegacyPosition_phase0 = async (
    network: Network,
    position: CLPosition,
    _fee: number,
    rangePercent: number
): Promise<{ success: boolean; error?: string; tx: Transaction | undefined }> => {
    const starknet = window.getStarknet?.()

    // ------------------------------------------
    let activeAccount: string = walletAddress()
    if (!activeAccount) {
        return { success: false, tx: undefined }
    }
    if (!starknet?.isConnected) throw Error('starknet wallet not connected')
    // ------------------------------------------
    try {
        const calls = await migrateLegacyPosition_phase0_calls(network, activeAccount, position, _fee, rangePercent)
        // @ts-ignore
        let tx = await starknet.account.execute(calls.calls)

        const transaction: Transaction = {
            hash: tx.transaction_hash,
            network,
            status: 'new',
            type: 'migrate_legacy',
            dismiss: false,
            token1: '',
            token1Amount: '',
            token2: '',
            token2Amount: ''
        }

        return { tx: transaction, success: true }
    } catch (e: any) {
        console.log('YSYS error in phase0 migration: ', e)
    }
    return { success: false, tx: undefined }
}
export interface LegacyMigrationQuote {
    v1Amount0: string
    v1Amount1: string
    clAmount0: string
    clAmount1: string
    leftoverAmount0: string
    leftoverAmount1: string
}
export const simulateMigrateLegacyPosition_phase0 = async (
    network: Network,
    position: CLPosition, // this is the legacy position
    _fee: number,
    rangePercent: number
): Promise<LegacyMigrationQuote | undefined> => {
    const starknet = window.getStarknet?.()

    // ------------------------------------------
    let activeAccount: string = walletAddress()
    if (!activeAccount) {
        return undefined
    }
    if (!starknet?.isConnected) throw Error('starknet wallet not connected')
    // ------------------------------------------
    try {
        const cairoVersion = accountCairoVersion()
        const handler = cairoVersion === '1' ? transaction.fromCallsToExecuteCalldata_cairo1 : transaction.fromCallsToExecuteCalldata
        const raw_calls = await migrateLegacyPosition_phase0_calls(network, activeAccount, position, _fee, rangePercent)
        if (raw_calls === undefined) return undefined
        const calls = handler(raw_calls.calls)
        const nonce = await (await getProvider(network)).getNonceForAddress(activeAccount)
        let invocation: AccountInvocationItem = {
            signature: [],
            // @ts-ignore
            type: 'INVOKE_FUNCTION',
            nonce: nonce,
            entrypoint: '__execute__',
            contractAddress: activeAccount,
            calldata: calls,
            version: '340282366920938463463374607431768211457'
        }
        const isBraavosMultisig = accountBraavosMultisig()
        if (isBraavosMultisig) {
            const wrapped_calls = handler([
                {
                    contractAddress: activeAccount,
                    entrypoint: 'sign_pending_multisig_transaction',
                    calldata: [calls.length, ...calls, 0, 0, 1]
                }
            ])
            invocation = {
                signature: ['0', '0'],
                // @ts-ignore
                type: 'INVOKE_FUNCTION',
                nonce: nonce,
                entrypoint: '__execute__',
                contractAddress: activeAccount,
                calldata: wrapped_calls,
                version: '340282366920938463463374607431768211457'
            }
        }

        // in case it is a BraavosMultisig - we need to use the sequencer
        // as the rpc nodes dont support simulate txn version
        const provider = await getProvider(network, isBraavosMultisig)

        const response = await provider.getSimulateTransaction([invocation], {
            blockIdentifier: 'pending',
            skipValidate: true
        })

        // the result from the sequencer is different than the result
        // from the rpc
        // @ts-ignore
        const res = isBraavosMultisig ? response[0].transaction_trace.function_invocation.result : response[0].transaction_trace.execute_invocation.result
        console.log('YSYSYS migrate response=', res)

        let idx = cairoVersion === '1' ? 4 : 2
        const token0AmountweiFromV1 = fromUint256(res[idx], res[idx + 1])
        const token1AmountweiFromV1 = fromUint256(res[idx + 2], res[idx + 3])

        idx = res.length - 4
        const token0Amountwei = fromUint256(res[idx], res[idx + 1])
        const token1Amountwei = fromUint256(res[idx + 2], res[idx + 3])

        // these are the legacy pool tokens (by legacy pool order - which basically should be the same as new pool)
        let token0 = getToken(network, position.token0)!
        let token1 = getToken(network, position.token1)!

        return {
            v1Amount0: toDisplay(token0AmountweiFromV1, token0),
            v1Amount1: toDisplay(token1AmountweiFromV1, token1),
            clAmount0: toDisplay(token0Amountwei, token0),
            clAmount1: toDisplay(token1Amountwei, token1),
            leftoverAmount0: toDisplay(BigNumber(token0AmountweiFromV1).minus(token0Amountwei), token0),
            leftoverAmount1: toDisplay(BigNumber(token1AmountweiFromV1).minus(token1Amountwei), token1)
        }
    } catch (e: any) {
        console.log('YSYS error in phase0 migration: ', e)
    }
    return undefined
}
